import { useState } from "react";
import { useSearchParams } from "react-router";
import { Download, MousePointerClick, Eye, Smartphone, UserRound, Zap } from "lucide-react";
import { useEvents, type EventRecord } from "../lib/api";
import { useCan } from "../lib/access";
import { useStatsParams } from "../lib/params";
import { countryName, flag, fmtMoney, fmtNumber, fmtTime, visitorLabel } from "../lib/format";
import { AppHeader, useAppId } from "../components/app-header";
import { StatsToolbar } from "../components/layout";
import { Ago, Badge, Button, Card, CardHeader, Drawing, Empty, ErrorState, Modal, Skeleton, Table, TableScroll, toaster } from "../components/ui";

function EventIcon({ e }: { e: EventRecord }) {
  const Icon = e.type === "page" ? Eye : e.type === "screen" ? Smartphone : e.type === "identify" ? UserRound : e.name.startsWith("$") ? Zap : MousePointerClick;
  return <Icon size={14} className="shrink-0 text-ink-3" aria-hidden />;
}

function describe(e: EventRecord) {
  if (e.type === "page" || e.type === "screen") return e.path ?? e.title ?? e.name;
  if (e.name === "$web_vital" && e.properties) return `${String(e.properties.metric)} ${String(e.properties.value)} (${String(e.properties.rating)})`;
  return e.name;
}

/** Live stream of raw events, and a per-visitor timeline for qualitative UX research. */
export function Events() {
  const appId = useAppId();
  const events = useEvents(appId);
  // ?visitor= opens a timeline directly (linked from surveys, feedback and replays).
  const [params, setParams] = useSearchParams();
  const visitor = params.get("visitor");
  const setVisitor = (v: string | null) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set("visitor", v);
      else next.delete("visitor");
      return next;
    });

  return (
    <>
      <AppHeader title="Events" />
      <StatsToolbar appId={appId} />
      <Card>
        <CardHeader
          title="Live events"
          subtitle="Newest first, refreshed every 10 seconds. Click a visitor to see their full journey."
          actions={<ExportButtons appId={appId} />}
        />
        {events.isError ? (
          <ErrorState error={events.error} />
        ) : events.isPending ? (
          <Skeleton className="m-4 h-96" />
        ) : events.data?.length ? (
          <EventTable rows={events.data} onVisitor={setVisitor} />
        ) : (
          <Empty title="No events yet" illustration={<Drawing name="firstData" />}>Install the SDK from this app's Settings page, then open your site.</Empty>
        )}
      </Card>
      <Modal open={!!visitor} onOpenChange={(o) => !o && setVisitor(null)} title="Visitor journey" description={visitor ?? undefined} wide>
        {visitor && <VisitorTimeline appId={appId} visitor={visitor} />}
      </Modal>
    </>
  );
}

/**
 * Downloads every event in the chosen range and filters. Fetched rather than linked, so a range
 * that is too big shows a message instead of saving an error as a file.
 */
function ExportButtons({ appId }: { appId: string }) {
  const can = useCan(appId);
  const p = useStatsParams();
  const [busy, setBusy] = useState<"csv" | "ndjson" | null>(null);
  if (!can("data.export")) return null;

  const download = async (format: "csv" | "ndjson") => {
    setBusy(format);
    try {
      const res = await fetch(`/api/v1/apps/${appId}/export?${new URLSearchParams({ ...p.query, format })}`, { credentials: "include" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; count?: number; max?: number };
        toaster.create({
          type: "error",
          title: "Export didn't start",
          description:
            body.error === "export_too_large"
              ? `This range has ${fmtNumber(body.count ?? 0)} events; one export holds up to ${fmtNumber(body.max ?? 0)}. Pick a shorter range or add a filter.`
              : "Something went wrong. Try again.",
        });
        return;
      }
      const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `events.${format}`;
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toaster.create({ type: "success", title: `Exported ${fmtNumber(Number(res.headers.get("x-export-rows") ?? 0))} events`, description: name });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div role="group" aria-label="Export events in this range" className="flex gap-2">
      <Button size="sm" onClick={() => download("csv")} disabled={!!busy} aria-busy={busy === "csv"}>
        <Download size={14} aria-hidden />
        {busy === "csv" ? "Exporting…" : "Export CSV"}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => download("ndjson")} disabled={!!busy} aria-busy={busy === "ndjson"} aria-label={busy === "ndjson" ? undefined : "Export NDJSON"}>
        {busy === "ndjson" ? "Exporting…" : "NDJSON"}
      </Button>
    </div>
  );
}

function EventTable({ rows, onVisitor }: { rows: EventRecord[]; onVisitor: (v: string) => void }) {
  return (
    <TableScroll label="Events" divided>
      <Table caption="Recent events, newest first. Select a visitor to see their timeline." className="min-w-[760px] text-[13px]">
        <thead>
          <tr>
            <th scope="col">Event</th>
            <th scope="col">Visitor</th>
            <th scope="col">Source</th>
            <th scope="col">Location</th>
            <th scope="col">Device</th>
            <th scope="col" className="text-right">Time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="hover:bg-surface-2">
              <td>
                <div className="flex items-center gap-2">
                  <EventIcon e={e} />
                  <span className="max-w-72 truncate font-mono text-ink">{describe(e)}</span>
                  {e.value != null && <Badge tone="good">{fmtMoney(e.value)}</Badge>}
                </div>
              </td>
              <td>
                <Button variant="link" className="max-w-40 font-mono text-xs" onClick={() => onVisitor(e.user_id ?? e.visitor_id)}>
                  <span className="truncate">{visitorLabel(e.visitor_id, e.user_id).slice(0, 24)}</span>
                </Button>
              </td>
              <td className="text-ink-2">{e.utm_source ?? e.referrer_domain ?? "Direct"}</td>
              <td className="text-ink-2">{e.country ? <>{flag(e.country)} {e.city ?? countryName(e.country)}</> : "—"}</td>
              <td className="text-ink-2">{[e.device_type, e.os, e.browser].filter(Boolean).join(" · ") || e.platform}</td>
              <td className="text-right text-ink-3 tabular"><Ago t={e.ts} /></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}

export function VisitorTimeline({ appId, visitor }: { appId: string; visitor: string }) {
  const q = useEvents(appId, visitor);
  if (q.isError) return <ErrorState title="Couldn't load this visitor's journey" error={q.error} />;
  if (q.isPending) return <Skeleton className="h-64" />;
  const rows = [...(q.data ?? [])].reverse();
  const first = rows[0];
  // Group into sessions for readability.
  const sessions: EventRecord[][] = [];
  for (const e of rows) {
    const last = sessions[sessions.length - 1];
    if (last && last[0]!.session_id === e.session_id) last.push(e);
    else sessions.push([e]);
  }
  return (
    <div className="max-h-[60vh] space-y-4 overflow-y-auto">
      {first && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {first.country && <Badge>{flag(first.country)} {countryName(first.country)}</Badge>}
          {first.device_type && <Badge>{first.device_type}</Badge>}
          {first.os && <Badge>{first.os}</Badge>}
          {first.browser && <Badge>{first.browser}</Badge>}
          <Badge>{sessions.length} session{sessions.length === 1 ? "" : "s"}</Badge>
          <Badge>{rows.length} events</Badge>
        </div>
      )}
      {sessions.map((s, i) => (
        <div key={i}>
          <div className="mb-1.5 text-xs font-medium text-ink-2">
            Session {i + 1} · {fmtTime(s[0]!.ts)} · via {s[0]!.utm_source ?? s[0]!.referrer_domain ?? "direct"}
          </div>
          <ol className="relative ml-2 space-y-1.5 border-l border-line pl-4">
            {s.map((e) => (
              <li key={e.id} className="relative">
                <span className="absolute top-1.5 -left-[21px] h-2 w-2 rounded-full ring-2 ring-surface" style={{ background: e.type === "page" || e.type === "screen" ? "var(--series-1)" : "var(--series-2)" }} />
                <div className="flex items-baseline justify-between gap-3 text-[13px]">
                  <span className="min-w-0 truncate font-mono text-ink">{describe(e)}</span>
                  <span className="shrink-0 text-xs text-ink-3 tabular">{new Date(e.ts).toLocaleTimeString()}</span>
                </div>
                {e.properties && Object.keys(e.properties).length > 0 && e.name !== "$web_vital" && (
                  <div className="truncate font-mono text-[11px] text-ink-3">{JSON.stringify(e.properties)}</div>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
      <p className="text-xs text-ink-3">Showing up to 100 events in the selected range.</p>
    </div>
  );
}
